import React, { useState, useEffect } from 'react';
import apiClient from '../api/client';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { Building, MapPin, Hash, User } from 'lucide-react';
import { driver } from 'driver.js';
import 'driver.js/dist/driver.css';

const Settings = () => {
  const { user, checkAuth } = useAuth();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    company: '',
    phone: '',
    gstin: '',
    address: '',
    currency: 'INR',
    country: 'India'
  });

  useEffect(() => {
    if (user) {
      setFormData({
        name: user.name || '',
        company: user.company || '',
        phone: user.phone || '',
        gstin: user.gstin || '',
        address: user.address || '',
        currency: user.currency || 'INR',
        country: user.country || 'India'
      });
    }
  }, [user]);

  useEffect(() => {
    if (user) {
      const hasSeenTour = localStorage.getItem('hasSeenSettingsTour');
      if (!hasSeenTour) {
        const driverObj = driver({
          showProgress: true,
          steps: [
            {
              element: '[data-testid="business-profile-card"]',
              popover: {
                title: 'Business Profile',
                description: 'Keep your agency details up to date. These details are used for auto-generating invoices and contracts.',
                side: "top",
                align: 'start'
              }
            },
            {
              element: '[data-testid="subscription-plan-card"]',
              popover: {
                title: 'Your Plan',
                description: 'View your current subscription and manage your billing details.',
                side: "top",
                align: 'start'
              }
            }
          ],
          onDestroyed: () => {
            localStorage.setItem('hasSeenSettingsTour', 'true');
          }
        });
        setTimeout(() => driverObj.drive(), 1000);
      }
    }
  }, [user]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await apiClient.put('/auth/me', formData);
      toast.success('Profile updated successfully');
      await checkAuth();
    } catch (error) {
      toast.error('Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-slate-900">Settings</h1>
        <p className="text-slate-600 mt-1">Manage your business profile and GST details</p>
      </div>

      <Card className="border-slate-100" data-testid="business-profile-card">
        <CardHeader>
          <CardTitle>Business Profile</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label>Country</Label>
                <Select value={formData.country} onValueChange={(value) => setFormData({ ...formData, country: value })}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select Country" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="India">India</SelectItem>
                    <SelectItem value="United States">United States</SelectItem>
                    <SelectItem value="United Kingdom">United Kingdom</SelectItem>
                    <SelectItem value="Other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Default Currency</Label>
                <Select value={formData.currency} onValueChange={(value) => setFormData({ ...formData, currency: value })}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select Currency" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="INR">INR (₹)</SelectItem>
                    <SelectItem value="USD">USD ($)</SelectItem>
                    <SelectItem value="GBP">GBP (£)</SelectItem>
                    <SelectItem value="EUR">EUR (€)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <User className="h-4 w-4 text-slate-400" /> Full Name
                </Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Your Name"
                />
              </div>
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <Building className="h-4 w-4 text-slate-400" /> Agency/Company Name
                </Label>
                <Input
                  value={formData.company}
                  onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                  placeholder="e.g. Acme Agency"
                />
              </div>
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <Hash className="h-4 w-4 text-slate-400" /> GSTIN (Section 31 Compliant)
                </Label>
                <Input
                  value={formData.gstin}
                  onChange={(e) => setFormData({ ...formData, gstin: e.target.value })}
                  placeholder="e.g. 27AAAAA0000A1Z5"
                  className="font-mono uppercase"
                />
              </div>
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <MapPin className="h-4 w-4 text-slate-400" /> Phone Number
                </Label>
                <Input
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  placeholder="+91 9876543210"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <MapPin className="h-4 w-4 text-slate-400" /> Registered Business Address
              </Label>
              <Textarea
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                placeholder="Full business address for invoicing"
                rows={3}
              />
            </div>

            <div className="flex justify-end pt-4">
              <Button type="submit" className="bg-indigo-600 hover:bg-indigo-700" disabled={loading}>
                {loading ? 'Saving...' : 'Save Settings'}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      <Card className="border-slate-100 bg-indigo-50/20 border-indigo-100" data-testid="subscription-plan-card">
        <CardContent className="pt-6">
          <div className="flex items-start gap-4">
            <div className="p-2 bg-indigo-100 rounded-lg">
              <Crown className="h-5 w-5 text-indigo-600" />
            </div>
            <div>
              <h4 className="font-bold text-slate-900">Current Plan: {user?.plan?.name || user?.plan_id?.toUpperCase()}</h4>
              <p className="text-sm text-slate-600 mt-1">
                {user?.plan?.description || 'Manage your subscription and billing details.'}
              </p>
              <Button
                variant="link"
                className="text-indigo-600 p-0 h-auto text-sm mt-2"
                onClick={() => window.location.href = '/pricing'}
              >
                View Plans & Billing
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// Add Crown icon import for the above component
const Crown = ({ className }) => (
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}><path d="m2 4 3 12h14l3-12-6 7-4-7-4 7-6-7zm3 16h14"/></svg>
);

export default Settings;
